// Datakilden — lenken til ULL-broen, og ærligheten om at den ikke finnes ennå.
//
// ══ TO HELT ULIKE LENKER, OG HVORFOR DET ER VIKTIG HER ══════════════════════
// Denne fila er den LOKALE lenken: broen står i lagets eget skjulsted og tar imot
// UGS-meshen. Den er kort rekkevidde og innkommende, og den er IKKE det som
// eksponerer laget.
//
// Lenken INN TIL SKADE er noe annet: den stråler, og den er derfor bak
// EMCON-bryteren i `main.ts`. `send()` her er bare transporten; om det får sendes
// i det hele tatt avgjøres av bryteren, og aldri av denne fila. En app som sender
// automatisk er en sender, og et jegerlag bak fiendens linjer skal se når det
// stråler.
//
// ══ VALIDERING: KONTRAKTENS EGET SKJEMA, IKKE HÅNDSKREVNE VAKTER ════════════
// NJORD måtte skrive vaktene for hånd, fordi KOP-kontrakten ikke finnes.
// UGS-kontrakten FINNES, og `UgsMelding` er et zod-skjema. Derfor parses de fem
// kontraktsmeldingene med `UgsMelding.safeParse` — da får vi hele
// `superRefine`-logikken gratis (art mot geometri, «kamera må ha sektor»,
// «menneskeligVurdering krever vurdertAvMenneske», tidsusikkerhet > 0). En
// håndskrevet vakt ville sluppet gjennom nettopp de meldingene kontrakten er
// skrevet for å stoppe.
//
// Bare ULLs to LOKALE utvidelser (`lag`, `rute`) valideres for hånd, og bare til
// de er flyttet inn i kontrakten.
import { UgsMelding } from '@skade/contracts';
import type { LatLon } from '@skade/contracts';
import type { Ovelsestilkobling } from '@skade/sensorflate';
import type { Rute, Ulllag, UllKommando, UllMelding } from './typer.js';

/**
 * `ikke-konfigurert` bærer TRE tekster og ikke én, og rekkefølgen er hierarkiet
 * fra `manglerKilde` i `@skade/sensorflate`:
 *
 *   `forklaring` — norsk, hel setning, det jegeren leser FØRST.
 *   `neste`      — hva han gjør nå. Uten dette er ærligheten hjelpeløs.
 *   `variabel`   — byggvariabelen, for DRIFT. Dempet, men aldri fjernet.
 *
 * Før sto variabelnavnet i `adresse`, og skjermbildet ble dominert av
 * `Forsøkt: (VITE_ULL_WS er ikke satt)`. Et jegerlag kan ikke sette en
 * byggvariabel i skjulstedet, og navnet forteller ingenting operativt. `adresse`
 * er derfor TOM i denne tilstanden — det finnes ingen adresse vi har forsøkt.
 */
export type Tilkobling =
  /** Ingen adresse satt. Ikke en feil — broen er ikke rullet ut. */
  | { tilstand: 'ikke-konfigurert'; adresse: ''; forklaring: string; neste: string; variabel: string }
  | { tilstand: 'kobler'; adresse: string }
  | { tilstand: 'tilkoblet'; adresse: string; sidenMs: number }
  /** Vi har mistet (eller aldri fått) forbindelsen. `arsak` vises ordrett. */
  | { tilstand: 'nede'; adresse: string; arsak: string; forsok: number }
  /**
   * ØVINGSDATA. Delt tilstand fra `@skade/sensorflate` (`OVELSE_TILKOBLING`), og
   * den later ALDRI som den er tilkoblet. Var tidligere en lokal `demo`-variant
   * med bare en `adresse`; den delte bærer også en `forklaring`, som er det
   * skjermene faktisk trenger å vise.
   */
  | Ovelsestilkobling;

export interface Kilde {
  tilkobling(): Tilkobling;
  /** Abonner på meldinger. Returnerer avmelding. */
  påMelding(h: (m: UllMelding) => void): () => void;
  /** Abonner på tilkoblingsendringer. Returnerer avmelding. */
  påTilkobling(h: (t: Tilkobling) => void): () => void;
  /** Send en kommando. `false` = kunne ikke sendes NÅ, og kalleren må si det. */
  send(k: UllKommando): boolean;
  lukk(): void;
}

/**
 * Adressen til ULL-broen. Tom verdi er en GYLDIG og forventet tilstand i dag,
 * ikke en feil å skjule — mates inn ved bygg, som `VITE_KOP_WS` i NJORD.
 */
export const ULL_WS = (import.meta.env['VITE_ULL_WS'] ?? '').trim();

/**
 * Basisadressen til mediatjenesten som slår opp `bildeId` → bilde.
 *
 * Den står HER og ikke i kontrakten fordi kontrakten med vilje ikke inneholder
 * URL-er: `BildeReferanse` avviser aktivt `://`, `@` og `/` i en bildeId, slik at
 * ingen kan lekke et internt vertsnavn eller en signert URL gjennom et felt som
 * heter «bilde». Klienten setter sammen adressen selv, av sin egen konfigurasjon.
 *
 * Er den tom, kan bildekøen IKKE vise bilder — og da skal den si det og nekte å
 * ta imot en vurdering. Se `skjermer/bilde.ts`.
 */
export const ULL_MEDIA = (import.meta.env['VITE_ULL_MEDIA'] ?? '').trim();

/**
 * Tekstene for «ingen bro». De ligger her — der adressen bor — fordi de er en del
 * av kildens tilstand, ikke av et skjermbilde: to paneler som beskriver samme
 * mangel med to ulike setninger er to sannheter.
 */
const IKKE_KONFIGURERT: Omit<Extract<Tilkobling, { tilstand: 'ikke-konfigurert' }>, 'tilstand'> = {
  adresse: '',
  forklaring: 'Ingen sensorbro er satt opp for dette laget ennå',
  neste:
    'Dette er ikke noe du kan rette fra skjermen — broen må settes opp på boksen i skjulstedet. Meld '
    + 'variabelnavnet under til drift. I mellomtiden kan du slå på ØVINGSDATA og trene på sekvensanalysen; '
    + 'den er tydelig merket, og ingenting fra den kan sendes til SKADE.',
  variabel: 'For drift: byggvariabelen VITE_ULL_WS er ikke satt (adressen til ULL-broens WebSocket)',
};

/**
 * Koble til broen. Reconnect med voksende pause (1 s → 30 s), som i NJORD:
 * grunnloven §6, «anta dårlig samband».
 *
 * MERK at gjentatte reconnect-forsøk mot en LOKAL bro i eget skjulsted er
 * akseptabelt — det er ikke denne lenken som peiles inn. Skulle broen en dag
 * ligge et annet sted, må denne løkka under EMCON-bryteren også.
 */
export function lagKilde(adresse = ULL_WS): Kilde {
  const meldingsLyttere = new Set<(m: UllMelding) => void>();
  const tilkoblingsLyttere = new Set<(t: Tilkobling) => void>();
  let ws: WebSocket | null = null;
  let lukket = false;
  let forsok = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let tilstand: Tilkobling = adresse
    ? { tilstand: 'kobler', adresse }
    : { tilstand: 'ikke-konfigurert', ...IKKE_KONFIGURERT };

  function sett(t: Tilkobling): void {
    tilstand = t;
    for (const h of tilkoblingsLyttere) h(t);
  }

  function koble(): void {
    if (lukket || !adresse) return;
    sett({ tilstand: 'kobler', adresse });
    let sokkel: WebSocket;
    try {
      sokkel = new WebSocket(adresse);
    } catch (e) {
      // En ugyldig URL kaster synkront. Da er det ikke sambandet som er nede —
      // det er konfigurasjonen, og den skal leses ordrett.
      sett({ tilstand: 'nede', adresse, arsak: `Ugyldig adresse: ${feiltekst(e)}`, forsok: ++forsok });
      return;
    }
    ws = sokkel;
    sokkel.addEventListener('open', () => {
      forsok = 0;
      sett({ tilstand: 'tilkoblet', adresse, sidenMs: Date.now() });
    });
    sokkel.addEventListener('message', (ev) => {
      const m = tolkMelding(ev.data);
      if (!m) return;
      for (const h of meldingsLyttere) h(m);
    });
    sokkel.addEventListener('error', () => {
      // `error` gir aldri en grunn i nettleseren. Å finne på en ville vært verre
      // enn å innrømme at vi ikke vet.
      sett({ tilstand: 'nede', adresse, arsak: 'Nettleseren rapporterte en tilkoblingsfeil uten årsak', forsok: forsok + 1 });
    });
    sokkel.addEventListener('close', (ev) => {
      if (lukket) return;
      forsok++;
      sett({
        tilstand: 'nede',
        adresse,
        arsak: ev.reason || `Forbindelsen lukket (kode ${ev.code})`,
        forsok,
      });
      const pause = Math.min(30_000, 1000 * 2 ** Math.min(forsok, 5));
      timer = globalThis.setTimeout(koble, pause);
    });
  }

  koble();

  return {
    tilkobling: () => tilstand,
    påMelding(h) { meldingsLyttere.add(h); return () => meldingsLyttere.delete(h); },
    påTilkobling(h) { tilkoblingsLyttere.add(h); return () => tilkoblingsLyttere.delete(h); },
    send(k) {
      if (!ws || ws.readyState !== WebSocket.OPEN) return false;
      try { ws.send(JSON.stringify(k)); return true; } catch { return false; }
    },
    lukk() {
      lukket = true;
      if (timer !== null) globalThis.clearTimeout(timer);
      ws?.close();
    },
  };
}

function feiltekst(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

// ── Tolkning ────────────────────────────────────────────────────────────────

function erObjekt(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}
function tall(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}
function tekst(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

/**
 * JSON → `UllMelding`, eller `null`.
 *
 * Rekkefølgen er bevisst: LOKALE utvidelser først (de kontrakten ikke kjenner),
 * deretter kontraktens eget skjema på ALT annet. Da kan en melding aldri både
 * bli tolket lokalt og av kontrakten, og den dagen `lag`/`rute` flyttes inn i
 * `ugs.ts`, slettes de to grenene og resten står uendret.
 */
export function tolkMelding(rå: unknown): UllMelding | null {
  let o: unknown = rå;
  if (typeof rå === 'string') {
    try { o = JSON.parse(rå); } catch { return null; }
  }
  if (!erObjekt(o) || !tekst(o['type'])) return null;

  if (o['type'] === 'lag') {
    const lag = erLag(o['lag']) ? o['lag'] : null;
    return lag ? { type: 'lag', lag } : null;
  }
  if (o['type'] === 'rute') {
    const rute = erRute(o['rute']) ? o['rute'] : null;
    return rute ? { type: 'rute', rute } : null;
  }

  // Kontraktens eget skjema. `safeParse` gir oss defaults (`aktiv: true`,
  // `vurdertAvMenneske: false`, `alternativer: []`) samtidig som den avviser —
  // det er nettopp derfor den brukes framfor en typevakt.
  const p = UgsMelding.safeParse(o);
  return p.success ? (p.data as UllMelding) : null;
}

/**
 * Laget. Bare `id` og `navn` er krav: et lag som ikke oppgir egen posisjon er
 * et lag som har god grunn til å ikke gjøre det, og det skal ikke gi avvisning.
 */
export function erLag(v: unknown): v is Ulllag {
  return erObjekt(v) && tekst(v['id']) && tekst(v['navn']);
}

/**
 * Ruta. Minst to punkt, og hvert punkt innenfor gyldig lat/lon — ellers ville
 * `dekningshull()` avvist den, og da er det bedre å avvise den her, der vi kan si
 * hvor den kom fra.
 */
export function erRute(v: unknown): v is Rute {
  if (!erObjekt(v) || !tekst(v['id']) || !tekst(v['navn'])) return false;
  const p = v['punkt'];
  if (!Array.isArray(p) || p.length < 2) return false;
  const gyldig = (x: unknown): x is LatLon =>
    erObjekt(x) && tall(x['lat']) && tall(x['lon']) && Math.abs(x['lat']) <= 90 && Math.abs(x['lon']) <= 180;
  if (!p.every(gyldig)) return false;
  return v['kilde'] === 'gått-med-gps' || v['kilde'] === 'tegnet-i-kart' || v['kilde'] === 'antatt';
}
